// utils/theme.js
const THEME_KEY = 'user-theme-preference'

const _mq = window.matchMedia('(prefers-color-scheme: dark)')
let _autoListenerAttached = false

const _handleAutoChange = () => {
	if (localStorage.getItem(THEME_KEY) === 'auto') {
		document.documentElement.setAttribute('data-theme', 'auto')
	}
}

function _attachAutoListener () {
	if (!_autoListenerAttached) {
		_mq.addEventListener('change', _handleAutoChange)
		_autoListenerAttached = true
	}
}

function _detachAutoListener () {
	if (_autoListenerAttached) {
		_mq.removeEventListener('change', _handleAutoChange)
		_autoListenerAttached = false
	}
}

/**
 * Initialize theme before Vue app loads
 * Reads theme preference from localStorage and applies it to the document
 * Supports 'light', 'dark', and 'auto' modes
 */
export const initializeTheme = () => {
	const savedTheme = localStorage.getItem(THEME_KEY) || 'auto'
	document.documentElement.setAttribute('data-theme', savedTheme)

	if (savedTheme === 'auto') {
		_attachAutoListener()
	}
}

/**
 * Get current theme preference
 * @returns {string} Current theme ('light', 'dark', or 'auto')
 */
export const getTheme = () => {
	return localStorage.getItem(THEME_KEY) || 'auto'
}

/**
 * Set theme preference
 * @param {string} theme - Theme to set ('light', 'dark', or 'auto')
 */
export const setTheme = (theme) => {
	localStorage.setItem(THEME_KEY, theme)
	document.documentElement.setAttribute('data-theme', theme)

	if (theme === 'auto') {
		_attachAutoListener()
	} else {
		_detachAutoListener()
	}
}

/**
 * Check if the current effective theme is dark
 * @returns {boolean} True if dark mode is active
 */
export const isDarkMode = () => {
	const theme = localStorage.getItem(THEME_KEY) || 'auto'
	if (theme === 'dark' || theme === 'nord') return true
	if (theme === 'light') return false
	return _mq.matches
}

let _colorProbe = null
let _colorCtx = null

/**
 * Resolve a theme CSS variable to an svg.js/ApexCharts-safe color string.
 *
 * Production CSS minification rewrites custom property values
 * (e.g. `rgba(255, 255, 255, 0.6)` -> `#fff9`), and the svg.js version
 * bundled inside ApexCharts cannot parse 4/8-digit hex colors — it throws
 * mid-render, leaving charts half-drawn. Normalizing through
 * `style.color` / canvas `fillStyle` always yields `rgb()`/`rgba()` or
 * `#rrggbb`, which parse correctly in every environment.
 *
 * @param {string} name - CSS variable name without the `--` prefix (e.g. 'text')
 * @param {string|null} fallback - Returned when the variable is missing/invalid
 * @returns {string|null}
 */
export const getThemeColor = (name, fallback = null) => {
	const raw = getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim()
	if (!raw) return fallback

	_colorProbe = _colorProbe || document.createElement('span')
	_colorProbe.style.color = ''
	_colorProbe.style.color = raw
	if (!_colorProbe.style.color) return fallback

	_colorCtx = _colorCtx || document.createElement('canvas').getContext('2d')
	if (!_colorCtx) return _colorProbe.style.color
	_colorCtx.fillStyle = raw
	return _colorCtx.fillStyle || fallback
}

export { THEME_KEY }
